import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, apiError, SITE_ORIGIN } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  PackageIcon,
  TruckIcon,
  CheckCircleIcon,
  ClockIcon,
  DownloadSimpleIcon,
  FileIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import OrderProgress from "@/components/OrderProgress";

const statusStyle = (s) => {
  const map = {
    paid: { cls: "bg-blue-100 text-[#165d85]", label: "Paid" },
    shipped: { cls: "bg-amber-100 text-amber-700", label: "Shipped" },
    delivered: { cls: "bg-emerald-100 text-emerald-700", label: "Delivered" },
    refunded: { cls: "bg-zinc-200 text-zinc-700", label: "Refunded" },
    cancelled: { cls: "bg-zinc-200 text-zinc-700", label: "Cancelled" },
  };
  return map[s] || { cls: "bg-zinc-100 text-zinc-700", label: s };
};

export default function Orders() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState([]);
  const [sales, setSales] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    (async () => {
      try {
        const [p, s, d] = await Promise.all([
          api.get("/orders", { params: { role: "buyer" } }),
          user.account_type.endsWith("_seller")
            ? api.get("/orders", { params: { role: "seller" } })
            : Promise.resolve({ data: { items: [] } }),
          api.get("/me/downloads").catch(() => ({ data: { items: [] } })),
        ]);
        setPurchases(p.data.items);
        setSales(s.data.items);
        setDownloads(d.data.items || []);
      } finally {
        setLoading(false);
      }
    })();
  }, [user, navigate]);

  if (!user) return null;
  const isSeller = user.account_type.endsWith("_seller");

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="orders-page">
      <Navbar />
      <main className="flex-1 max-w-6xl mx-auto w-full px-6 py-10">
        <p className="overline text-zinc-500">ORDERS</p>
        <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter mt-2">
          Your orders
        </h1>

        <Tabs defaultValue="purchases" className="mt-8">
          <TabsList className="bg-zinc-100 border border-zinc-200">
            <TabsTrigger value="purchases" data-testid="tab-purchases">
              Purchases ({purchases.length})
            </TabsTrigger>
            <TabsTrigger value="downloads" data-testid="tab-downloads">
              Downloads ({downloads.length})
            </TabsTrigger>
            {isSeller && (
              <TabsTrigger value="sales" data-testid="tab-sales">
                Sales ({sales.length})
              </TabsTrigger>
            )}
          </TabsList>
          <TabsContent value="purchases" className="mt-6">
            {loading ? (
              <p className="text-sm text-zinc-500">Loading…</p>
            ) : purchases.length === 0 ? (
              <Empty text="No purchases yet." />
            ) : (
              <OrderList items={purchases} role="buyer" />
            )}
          </TabsContent>
          <TabsContent value="downloads" className="mt-6">
            {loading ? (
              <p className="text-sm text-zinc-500">Loading…</p>
            ) : downloads.length === 0 ? (
              <Empty text="No digital purchases yet." />
            ) : (
              <DownloadsList items={downloads} />
            )}
          </TabsContent>
          {isSeller && (
            <TabsContent value="sales" className="mt-6">
              {sales.length === 0 ? (
                <Empty text="No sales yet." />
              ) : (
                <OrderList items={sales} role="seller" />
              )}
            </TabsContent>
          )}
        </Tabs>
      </main>
      <Footer />
    </div>
  );
}

function Empty({ text }) {
  return (
    <div className="border border-zinc-200 p-16 text-center bg-white">
      <PackageIcon size={36} className="mx-auto text-zinc-300" />
      <p className="font-display font-bold text-xl mt-4">{text}</p>
      <Link to="/browse" className="text-sm text-[#1D78AB] mt-2 inline-block">
        Browse marketplace →
      </Link>
    </div>
  );
}

function OrderList({ items, role }) {
  return (
    <div className="border border-zinc-200 divide-y divide-zinc-200 bg-white">
      {items.map((o) => {
        const s = statusStyle(o.status);
        return (
          <Link
            key={o.id}
            to={`/orders/${o.id}`}
            className="block p-4 hover:bg-zinc-50 transition-colors"
            data-testid={`order-row-${o.id}`}
          >
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 bg-zinc-100 flex-shrink-0">
                {o.listing_image && (
                  <img
                    src={`${SITE_ORIGIN}/api/files/${o.listing_image}`}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate">{o.listing_title}</p>
                <p className="text-xs text-zinc-500">
                  {role === "buyer" ? "Purchased" : "Sold"} ·{" "}
                  {new Date(o.created_at).toLocaleDateString()}
                </p>
              </div>
              <span
                className={`px-2 py-1 text-[10px] font-bold tracking-widest uppercase ${s.cls}`}
              >
                {s.label}
              </span>
              <span className="font-mono font-bold text-[#1D78AB] whitespace-nowrap">
                ${Number(o.amount).toFixed(2)}
              </span>
            </div>
            <div className="mt-3 pl-[72px] pr-4">
              <OrderProgress order={o} variant="compact" />
            </div>
          </Link>
        );
      })}
    </div>
  );
}

function DownloadsList({ items }) {
  const [busyId, setBusyId] = useState(null);

  const handleDownload = async (item) => {
    setBusyId(item.order_id);
    try {
      // Auth cookies are needed — fetch through the axios client so we
      // ride the same session. responseType=blob so we can hand a real
      // file object to the browser instead of getting a base64 string.
      const resp = await api.get(`/me/downloads/${item.order_id}/file`, {
        responseType: "blob",
      });
      const blob = new Blob([resp.data], {
        type: resp.headers["content-type"] || "application/octet-stream",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = item.filename || "download";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e) || "Download failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div
      className="border border-zinc-200 divide-y divide-zinc-200 bg-white"
      data-testid="downloads-list"
    >
      {items.map((it) => (
        <div
          key={it.order_id}
          className="p-4 flex items-center gap-4"
          data-testid={`download-row-${it.order_id}`}
        >
          <div className="h-14 w-14 bg-zinc-100 flex-shrink-0 grid place-items-center">
            {it.thumbnail ? (
              <img
                src={`${SITE_ORIGIN}/api/files/${it.thumbnail}`}
                alt=""
                className="h-full w-full object-cover"
              />
            ) : (
              <FileIcon size={26} className="text-zinc-400" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold truncate">{it.title}</p>
            <p className="text-xs text-zinc-500 truncate">
              {it.filename || "Digital file"} · {it.seller_name}
            </p>
          </div>
          <span className="font-mono font-bold text-[#1D78AB] hidden sm:inline">
            ${Number(it.amount).toFixed(2)}
          </span>
          <button
            type="button"
            onClick={() => handleDownload(it)}
            disabled={busyId === it.order_id}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#1D78AB] hover:bg-[#165d85] disabled:opacity-60 text-white text-xs font-bold px-3 py-2"
            data-testid={`download-btn-${it.order_id}`}
          >
            <DownloadSimpleIcon size={14} weight="bold" />
            {busyId === it.order_id ? "Downloading…" : "Download"}
          </button>
        </div>
      ))}
    </div>
  );
}

function StatusIcon({ status }) {
  // Kept only because OrderProgress now handles all visual status
  // indication; left as a no-op for any external usage that might
  // still reference this component.
  return null;
}
